import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  TextInput,
  ScrollView,
  Image,
  Modal,
} from 'react-native';
import { MaterialIcons as Icon } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useLanguage } from '@context';

interface Contact {
  id: string;
  name: string;
  avatar: string;
  selected: boolean;
}

export const AddSubcategory: React.FC = () => {
  const navigation = useNavigation();
  const { t } = useLanguage();
  const [subcategoryTitle, setSubcategoryTitle] = useState('');
  const [selectedContacts, setSelectedContacts] = useState<Contact[]>([]);
  const [showContactModal, setShowContactModal] = useState(false);

  // Données de test pour les contacts
  const mockContacts: Contact[] = [
    { id: '1', name: 'Olivier Benoit', avatar: 'https://i.pravatar.cc/150?img=1', selected: false },
    { id: '2', name: 'Marie Dubois', avatar: 'https://i.pravatar.cc/150?img=2', selected: false },
    { id: '3', name: 'Sophie Martin', avatar: 'https://i.pravatar.cc/150?img=3', selected: false },
    { id: '4', name: 'Lucas Moreau', avatar: 'https://i.pravatar.cc/150?img=4', selected: false },
  ];

  const [contacts, setContacts] = useState<Contact[]>(mockContacts);

  const handleBack = () => {
    navigation.goBack();
  };

  const handleSave = () => {
    console.log('Sauvegarder la sous-catégorie:', subcategoryTitle, selectedContacts);
    navigation.goBack();
  };

  const handleAddPhoto = () => {
    console.log('Ajouter une photo');
  };

  const handleAddContact = () => {
    setShowContactModal(true);
  };

  const toggleContactSelection = (contactId: string) => {
    setContacts(prevContacts =>
      prevContacts.map(contact =>
        contact.id === contactId
          ? { ...contact, selected: !contact.selected }
          : contact
      )
    );
  };

  const handleContactModalSave = () => {
    const selected = contacts.filter(contact => contact.selected);
    setSelectedContacts(selected);
    setShowContactModal(false);
  };

  const renderContactItem = (contact: Contact) => (
    <TouchableOpacity
      key={contact.id}
      style={styles.contactItem}
      onPress={() => toggleContactSelection(contact.id)}
    >
      <Image source={{ uri: contact.avatar }} style={styles.contactAvatar} />
      <Text style={styles.contactName}>{contact.name}</Text>
      <View style={[styles.checkbox, contact.selected && styles.checkboxSelected]}>
        {contact.selected && <Icon name="check" size={16} color="white" />}
      </View>
    </TouchableOpacity>
  );

  const renderSelectedContact = (contact: Contact) => (
    <TouchableOpacity
      key={contact.id}
      style={styles.selectedContactItem}
      onPress={() => {
        setSelectedContacts(prev => prev.filter(c => c.id !== contact.id));
        setContacts(prev =>
          prev.map(c => c.id === contact.id ? { ...c, selected: false } : c)
        );
      }}
    >
      <Image source={{ uri: contact.avatar }} style={styles.selectedContactAvatar} />
      <Text style={styles.selectedContactName}>{contact.name}</Text>
      <View style={styles.removeButton}>
        <Icon name="close" size={16} color="#8E8E93" />
      </View>
    </TouchableOpacity>
  );

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity style={styles.backButton} onPress={handleBack}>
          <Icon name="arrow-back-ios" size={20} color="#007AFF" />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>{t('categoriesScreen.addSubcategory')}</Text>
        <View style={styles.placeholder} />
      </View>

      <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
        {/* Photo de la sous-catégorie */}
        <TouchableOpacity style={styles.photoCard} onPress={handleAddPhoto}>
          <View style={styles.photoPlaceholder}>
            <Icon name="cloud-upload" size={40} color="#8E8E93" />
            <Text style={styles.photoText}>{t('categoriesScreen.subcategoryPhoto')}</Text>
          </View>
        </TouchableOpacity>

        {/* Titre de la sous-catégorie */}
        <View style={styles.titleContainer}>
          <TextInput
            style={styles.titleInput}
            placeholder={t('categoriesScreen.subcategoryTitlePlaceholder')}
            value={subcategoryTitle}
            onChangeText={setSubcategoryTitle}
            placeholderTextColor="#8E8E93"
          />
          <Icon name="edit" size={20} color="#8E8E93" />
        </View>

        {/* Ajouter des contacts */}
        <TouchableOpacity style={styles.addContactButton} onPress={handleAddContact}>
          <Icon name="group-add" size={20} color="#007AFF" />
          <View style={styles.addContactText}>
            <Text style={styles.addContactTitle}>{t('menuContactsList.addContacts')}</Text>
            <Text style={styles.addContactSubtitle}>{t('common.selectedCount', { count: selectedContacts.length })}</Text>
          </View>
        </TouchableOpacity>

        {/* Contacts sélectionnés */}
        {selectedContacts.length > 0 && (
          <View style={styles.selectedContactsContainer}>
            {selectedContacts.map(renderSelectedContact)}
          </View>
        )}

        {/* Filtres */}
        <View style={styles.filtersContainer}>
          <TouchableOpacity style={[styles.filterButton, styles.activeFilter]}>
            <Icon name="lock" size={16} color="#007AFF" />
            <Text style={[styles.filterText, styles.activeFilterText]}>{t('common.private')}</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.filterButton}>
            <Icon name="business" size={16} color="#8E8E93" />
            <Text style={styles.filterText}>{t('common.pro')}</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.filterButton}>
            <Icon name="lock-open" size={16} color="#8E8E93" />
          </TouchableOpacity>
          <TouchableOpacity style={styles.filterButton}>
            <Text style={styles.hashtagText}>#</Text>
          </TouchableOpacity>
        </View>
      </ScrollView>

      {/* Bouton Sauvegarder */}
      <View style={styles.footer}>
        <TouchableOpacity
          style={[styles.saveButton, !subcategoryTitle && styles.saveButtonDisabled]}
          onPress={handleSave}
          disabled={!subcategoryTitle}
        >
          <Text style={[styles.saveButtonText, !subcategoryTitle && styles.saveButtonTextDisabled]}>
            {t('common.save')}
          </Text>
        </TouchableOpacity>
      </View>

      {/* Modal de sélection des contacts */}
      <Modal
        visible={showContactModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowContactModal(false)}
      >
        <View style={styles.modalOverlay}>
          <TouchableOpacity 
            style={styles.overlayTouchable}
            activeOpacity={1}
            onPress={() => setShowContactModal(false)}
          />
          <View style={styles.modalContainer}>
            <View style={styles.handle} />
            
            <View style={styles.modalHeader}>
              {/* @TODO design */}
              <Text style={styles.modalTitle}>Sport</Text>
            </View>
            
            <Text style={styles.modalSubtitle}>{t('common.personCount', { count: 15 })}</Text>
            
            <ScrollView style={styles.modalContactsList}>
              {contacts.map(renderContactItem)}
            </ScrollView>
            
            <View style={styles.modalFooter}>
              <TouchableOpacity 
                style={styles.modalSaveButton}
                onPress={handleContactModalSave}
              >
                <Text style={styles.modalSaveButtonText}>{t('common.save')}</Text>
              </TouchableOpacity>
            </View>
            
            <SafeAreaView style={styles.safeArea} />
          </View>
        </View>
      </Modal>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F2F2F7',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: 'white',
    borderBottomWidth: 0.5,
    borderBottomColor: '#C7C7CC',
  },
  backButton: {
    padding: 8,
  },
  headerTitle: {
    fontSize: 17,
    fontWeight: '600',
    color: '#000',
  },
  placeholder: {
    width: 32,
  },
  content: {
    flex: 1,
    padding: 16,
  },
  photoCard: {
    backgroundColor: 'white',
    borderRadius: 12,
    marginBottom: 16,
    overflow: 'hidden',
  },
  photoPlaceholder: {
    height: 200,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#F8F9FA',
  },
  photoText: {
    fontSize: 16,
    color: '#8E8E93',
    marginTop: 8,
  },
  titleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'white',
    borderRadius: 25,
    paddingHorizontal: 16,
    paddingVertical: 12,
    marginBottom: 16,
  },
  titleInput: {
    flex: 1,
    fontSize: 16,
    color: '#000',
  },
  addContactButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
  },
  addContactText: {
    marginLeft: 12,
    flex: 1,
  },
  addContactTitle: {
    fontSize: 16,
    color: '#000',
    fontWeight: '500',
  },
  addContactSubtitle: {
    fontSize: 13,
    color: '#8E8E93',
    marginTop: 2,
  },
  selectedContactsContainer: {
    marginBottom: 16,
  },
  selectedContactItem: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 12,
    marginBottom: 8,
  },
  selectedContactAvatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
    marginRight: 12,
  },
  selectedContactName: {
    flex: 1,
    fontSize: 16,
    color: '#000',
  },
  removeButton: {
    padding: 4,
  },
  filtersContainer: {
    flexDirection: 'row',
    marginBottom: 16,
  },
  filterButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'white',
    borderRadius: 20,
    paddingHorizontal: 12,
    paddingVertical: 8,
    marginRight: 8,
  },
  activeFilter: {
    backgroundColor: '#E3F2FD',
  },
  filterText: {
    fontSize: 14,
    color: '#8E8E93',
    marginLeft: 4,
  },
  activeFilterText: {
    color: '#007AFF',
  },
  hashtagText: {
    fontSize: 16,
    color: '#8E8E93',
    fontWeight: 'bold',
  },
  footer: {
    padding: 16,
    backgroundColor: 'white',
  },
  saveButton: {
    backgroundColor: '#007AFF',
    borderRadius: 25,
    paddingVertical: 16,
    alignItems: 'center',
  },
  saveButtonDisabled: {
    backgroundColor: '#C7C7CC',
  },
  saveButtonText: {
    fontSize: 17,
    fontWeight: '600',
    color: 'white',
  },
  saveButtonTextDisabled: {
    color: '#8E8E93',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    justifyContent: 'flex-end',
  },
  modalContainer: {
    backgroundColor: 'white',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    minHeight: 200,
    maxHeight: '80%',
  },
  handle: {
    width: 36,
    height: 4,
    backgroundColor: '#C7C7CC',
    borderRadius: 2,
    alignSelf: 'center',
    marginTop: 8,
    marginBottom: 8,
  },
  modalHeader: {
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 0.5,
    borderBottomColor: '#C7C7CC',
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#000',
    textAlign: 'center',
  },
  modalSubtitle: {
    fontSize: 14,
    color: '#8E8E93',
    padding: 20,
    paddingTop: 10,
  },
  modalContactsList: {
    flex: 1,
  },
  contactItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 12,
  },
  contactAvatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    marginRight: 12,
  },
  contactName: {
    flex: 1,
    fontSize: 16,
    color: '#000',
  },
  checkbox: {
    width: 24,
    height: 24,
    borderRadius: 12,
    borderWidth: 2,
    borderColor: '#C7C7CC',
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxSelected: {
    backgroundColor: '#007AFF',
    borderColor: '#007AFF',
  },
  overlayTouchable: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  modalFooter: {
    padding: 20,
    borderTopWidth: 0.5,
    borderTopColor: '#C7C7CC',
  },
  modalSaveButton: {
    backgroundColor: '#007AFF',
    borderRadius: 25,
    paddingVertical: 16,
    alignItems: 'center',
  },
  modalSaveButtonText: {
    fontSize: 17,
    fontWeight: '600',
    color: 'white',
  },
  safeArea: {
    flex: 1,
  },
});

export default AddSubcategory; 